/**
 * Procedural wizards, projectiles, corpses and pillars (DESIGN 6.2 "Entity
 * visuals"), drawn upright in the 2.5D view: feet at the world position,
 * body rising on screen, an elliptical drop shadow on the ground.
 *
 * Three passes per frame (renderer.ts): ground marks (shadow, facing wedge,
 * status puddles, charge ring), the y-sorted body, then labels (name badge,
 * HP bar, 5-slot queue, status icons) so no body ever covers a label.
 */

import { BOULDER, PLAYER_RADIUS, PRIORITY, isBase, rockMuzzle, rockRadius, type Element, type Vec2 } from '@shared/index.js';
import type { ViewCorpse, ViewPlayer, ViewProjectile } from '../state/viewModel.js';
import { elementColor, ELEMENT_SWATCH, GROUND, greyed, hpColor, mixHex, playerColor, playerColorDark, rgba, UI } from '../palette.js';
import { circlePath, clamp01, drawGlow, drawShadow, font, hexPattern, outlinedText, PILLAR_H, polygonPath, roundRectPath, STAFF_TIP_H, TAU, UNIT, WIZARD_H, type Ctx } from './gfx.js';
import type { Camera } from './camera.js';
import { drawStatusAbove, drawStatusBelow, drawStatusIcons, statusTints, tintBody, type StatusLook } from './statusFx.js';
import { art, type Drawn, type SpriteSet } from './sprites.js';
import { playerClip } from './spriteSets.js';
import { drawRockArt, fxAt, fxTurned } from './spellArt.js';

const SKIN = '#F1D2B0';
const STAFF = '#6B4A1B';
const STAFF_LIGHT = '#B5752D';
/** Knockdown stand-up tween length in seconds. */
const STAND_UP_S = 0.3;

/** Highest-priority element in a queue (the form); null when empty. Local, so the HUD never calls the shared stub. */
export function dominantOf(q: readonly Element[]): Element | null {
  let best: Element | null = null;
  for (const e of q) if (best === null || PRIORITY[e] > PRIORITY[best]) best = e;
  return best;
}

/** Screen position of a wizard's staff tip for a given aim (beams and sprays start here). */
export function staffTip(cam: Camera, x: number, y: number, aim: number, out: Vec2): Vec2 {
  out.x = cam.sx(x + Math.cos(aim) * STAFF_TIP_H);
  out.y = cam.sy(y + Math.sin(aim) * STAFF_TIP_H) - STAFF_TIP_H * UNIT;
  return out;
}

// ------------------------------------------------------------------ wizard

/** Ground pass: shadow, facing wedge, status puddle/ring, charge ring. */
export function drawWizardGround(ctx: Ctx, cam: Camera, p: ViewPlayer, look: StatusLook, time: number): void {
  const px = cam.sx(p.rx), py = cam.sy(p.ry);
  const u = UNIT;
  drawShadow(ctx, px, py, 0.42 * u, 0.17 * u, p.isLocal ? 0.5 : 0.4);
  if (p.isLocal) {
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.ellipse(px, py, 0.55 * u, 0.23 * u, 0, 0, TAU);
    ctx.stroke();
  }
  drawStatusBelow(ctx, px, py, 0.42 * u, look, time);
  // facing wedge (reads which way the beam will go)
  const col = playerColor(p.color);
  ctx.fillStyle = rgba(col, p.isLocal ? 0.7 : 0.4);
  const a = p.facing;
  ctx.beginPath();
  ctx.moveTo(px + Math.cos(a) * 0.62 * u, py + Math.sin(a) * 0.62 * u * 0.45);
  ctx.lineTo(px + Math.cos(a + 2.4) * 0.3 * u, py + Math.sin(a + 2.4) * 0.3 * u * 0.45);
  ctx.lineTo(px + Math.cos(a - 2.4) * 0.3 * u, py + Math.sin(a - 2.4) * 0.3 * u * 0.45);
  ctx.closePath();
  ctx.fill();
  if (p.act === 'charge' && p.ch > 0) drawChargeRing(ctx, px, py, p.ch, p.q, time);
}

/** Growing charge ring under a wizard holding a projectile (DESIGN 6.4 "charge shown as a growing ring"). */
export function drawChargeRing(ctx: Ctx, px: number, py: number, charge: number, q: readonly Element[], time: number): void {
  const u = UNIT;
  const dom = dominantOf(q) ?? 'earth';
  const col = ELEMENT_SWATCH[dom === 'ice' || dom === 'lava' ? dom : 'earth'].core;
  const r = (0.5 + charge * 0.6) * u;
  ctx.strokeStyle = rgba(col, 0.35);
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.ellipse(px, py, r, r * 0.45, 0, 0, TAU);
  ctx.stroke();
  ctx.strokeStyle = charge >= 1 ? rgba('#FFFFFF', 0.6 + 0.4 * Math.sin(time * 20)) : col;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.ellipse(px, py, r, r * 0.45, 0, -Math.PI / 2, -Math.PI / 2 + TAU * charge);
  ctx.stroke();
}

/** What a held projectile is made of: a rock when it has earth (or lava) in it, else ice shards. */
function heldSolid(el: readonly Element[]): 'earth' | 'ice' {
  if (el.length === 0 || el.includes('earth') || el.includes('lava')) return 'earth';
  return el.includes('ice') ? 'ice' : 'earth';
}

/**
 * The rock growing in front of a wizard holding Force: at the muzzle and hand height the throw starts from
 * (rockThrow.ts), its size the charge's, drawn exactly like the thrown rock, so letting go never pops.
 */
function drawHeldRock(ctx: Ctx, cam: Camera, p: ViewPlayer, el: readonly Element[], time: number): void {
  const u = UNIT;
  const r = rockRadius(p.ch);
  const at = rockMuzzle({ x: p.rx, y: p.ry }, p.facing, PLAYER_RADIUS, r);
  const px = cam.sx(at.x), py = cam.sy(at.y) - BOULDER.handHeight * u;
  drawShadow(ctx, cam.sx(at.x), cam.sy(at.y), r * u, r * u * 0.45, 0.25);
  // gathering motes swirl in while it charges; a full charge throbs white
  const full = p.ch >= 1;
  ctx.globalCompositeOperation = 'lighter';
  drawGlow(ctx, full ? '#FFFFFF' : ELEMENT_SWATCH.earth.soft, px, py, r * u * (3 + (full ? Math.sin(time * 18) : 0)));
  ctx.globalCompositeOperation = 'source-over';
  drawRock(ctx, px, py, r * u, rockPayload(el), rockSpin(p.id, time));
}

/** A rock's payload colour: the first element riding it that isn't the rock itself. */
function rockPayload(el: readonly Element[]): Element | null {
  return el.find((e) => e !== 'earth') ?? null;
}

/** Spin of a caster's rock, the same in the hand and in the air. */
function rockSpin(owner: number, time: number): number {
  return time * 5 + owner;
}

/**
 * One rock: the effects library's pixel rock nearest its size (a lava one glowing through its cracks), else a
 * craggy heptagon rimmed in its payload's colour; anything riding it glows round it.
 */
function drawRock(ctx: Ctx, px: number, py: number, r: number, payload: Element | null, spin: number): void {
  if (drawRockArt(ctx, px, py, r, payload, spin)) {
    if (payload && payload !== 'lava') {
      ctx.globalCompositeOperation = 'lighter';
      drawGlow(ctx, elementColor(payload), px, py, r * 3);
      ctx.globalCompositeOperation = 'source-over';
    }
    return;
  }
  ctx.fillStyle = payload === 'lava' ? ELEMENT_SWATCH.lava.dark : ELEMENT_SWATCH.earth.core;
  polygonPath(ctx, px, py, r, 7, spin);
  ctx.fill();
  ctx.strokeStyle = payload ? elementColor(payload) : ELEMENT_SWATCH.earth.dark;
  ctx.lineWidth = payload ? 3 : 2;
  ctx.stroke();
  ctx.strokeStyle = rgba('#000000', 0.35);
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(px - r * 0.4, py - r * 0.2);
  ctx.lineTo(px + r * 0.1, py + r * 0.3);
  ctx.stroke();
  if (payload) {
    ctx.globalCompositeOperation = 'lighter';
    drawGlow(ctx, elementColor(payload), px, py, r * 3);
    ctx.globalCompositeOperation = 'source-over';
  }
}

/** Body pass. `flash` 0..1 whitens the body (hit flash); `tipColor` overrides the staff orb colour. */
export function drawWizard(ctx: Ctx, cam: Camera, p: ViewPlayer, look: StatusLook, flash: number, time: number): void {
  const u = UNIT;
  const px = cam.sx(p.rx) + look.jx, py = cam.sy(p.ry) + look.jy;
  const dir = Math.cos(p.facing) >= 0 ? 1 : -1;
  const base = playerColor(p.color);
  const robe = tintBody(base, look, flash);
  const robeDark = tintBody(playerColorDark(p.color), look, flash);
  const alpha = p.con === 0 ? 0.5 : 1;
  ctx.globalAlpha = alpha;

  // The baked wizard once its set has landed; the orb, glow and charge then sit on its staff tip.
  const set = art.set('wizard');
  const drawn = set ? drawWizardSprite(ctx, cam, set, p, look, flash, time) : null;
  let lying = 0;
  let hx = px, hy = py - 0.55 * u, swing = 0;
  let tipX = cam.sx(p.rx + Math.cos(p.facing) * STAFF_TIP_H) + look.jx;
  let tipY = cam.sy(p.ry + Math.sin(p.facing) * STAFF_TIP_H) - STAFF_TIP_H * u + look.jy;
  if (drawn) {
    const tip = set!.point('staff_tip', p.id);
    if (tip) { tipX = px + tip.x; tipY = py + tip.y; }
  } else {
    // Knockdown: lie down around the feet, fall backward; stand up over STAND_UP_S.
    if (look.knockdown) lying = clamp01(look.knockdownT / STAND_UP_S);
    if (lying > 0) {
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(-dir * lying * Math.PI * 0.5);
      ctx.translate(-px, -py);
    }
    const walk = look.anim > 0 && (p.vx !== 0 || p.vy !== 0) ? Math.sin(time * 14 * look.anim) * 1.5 : 0;

    // robe
    ctx.fillStyle = robe;
    ctx.beginPath();
    ctx.moveTo(px - 0.42 * u, py);
    ctx.quadraticCurveTo(px, py + 0.1 * u + walk, px + 0.42 * u, py);
    ctx.lineTo(px + 0.2 * u, py - 0.82 * u);
    ctx.quadraticCurveTo(px, py - 0.9 * u, px - 0.2 * u, py - 0.82 * u);
    ctx.closePath();
    ctx.fill();
    // hem band + belt
    ctx.fillStyle = robeDark;
    ctx.beginPath();
    ctx.moveTo(px - 0.42 * u, py);
    ctx.quadraticCurveTo(px, py + 0.1 * u + walk, px + 0.42 * u, py);
    ctx.lineTo(px + 0.38 * u, py - 0.12 * u);
    ctx.quadraticCurveTo(px, py - 0.02 * u + walk, px - 0.38 * u, py - 0.12 * u);
    ctx.closePath();
    ctx.fill();
    ctx.fillRect(px - 0.3 * u, py - 0.5 * u, 0.6 * u, 0.06 * u);
    // armor plates (DESIGN 2.6 rock / ice armor)
    if (p.arm !== 'none') drawArmor(ctx, px, py, u, p.arm);
    // head
    ctx.fillStyle = tintBody(SKIN, look, flash);
    circlePath(ctx, px + dir * 0.02 * u, py - 0.95 * u, 0.17 * u);
    ctx.fill();
    // hat: brim + cone leaning with facing
    ctx.fillStyle = robeDark;
    ctx.beginPath();
    ctx.ellipse(px, py - 1.03 * u, 0.36 * u, 0.09 * u, 0, 0, TAU);
    ctx.fill();
    ctx.fillStyle = robe;
    ctx.beginPath();
    ctx.moveTo(px - 0.26 * u, py - 1.04 * u);
    ctx.lineTo(px + 0.26 * u, py - 1.04 * u);
    ctx.lineTo(px + dir * 0.12 * u, py - 1.42 * u);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = mixHex(base, '#FFFFFF', 0.5);
    ctx.fillRect(px - 0.22 * u, py - 1.12 * u, 0.44 * u, 0.05 * u);
    // staff from the hand to the tip (world-anchored so beams line up)
    hx = px + dir * 0.3 * u;
    hy = py - 0.52 * u;
    swing = p.act === 'melee' ? Math.sin(time * 25) * 0.25 * u : 0;
    ctx.strokeStyle = STAFF;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(hx - (tipX - hx) * 0.55, hy - (tipY - hy) * 0.55 + swing);
    ctx.lineTo(tipX, tipY);
    ctx.stroke();
    ctx.strokeStyle = STAFF_LIGHT;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
  // An imbued staff glows along its length in the imbued elements' colours until the charges run out.
  if (p.imb && p.imb.length > 0) {
    ctx.globalCompositeOperation = 'lighter';
    const midX = (hx + tipX) / 2, midY = (hy + tipY) / 2 + swing / 2;
    for (let i = 0; i < p.imb.length; i++) {
      const col = elementColor(p.imb[i]!);
      const wob = Math.sin(time * 8 + i * 2.1) * 1.5;
      ctx.strokeStyle = rgba(col, 0.75);
      ctx.lineWidth = 4 - i * 0.6;
      ctx.beginPath();
      ctx.moveTo(midX + wob, midY);
      ctx.lineTo(tipX + wob, tipY);
      ctx.stroke();
    }
    drawGlow(ctx, elementColor(p.imb[0]!), midX, midY, 18 + Math.sin(time * 6) * 3);
    ctx.globalCompositeOperation = 'source-over';
  }
  const dom = dominantOf(p.q);
  const orb = dom ? elementColor(dom) : '#CBD5E1';
  const channeling = p.act === 'beam' || p.act === 'spray' || p.act === 'arc' || p.act === 'charge';
  if (channeling || dom) {
    ctx.globalCompositeOperation = 'lighter';
    drawGlow(ctx, orb, tipX, tipY, (channeling ? 26 : 16) + Math.sin(time * 9) * 3);
    ctx.globalCompositeOperation = 'source-over';
  }
  ctx.fillStyle = orb;
  circlePath(ctx, tipX, tipY, 0.09 * u);
  ctx.fill();
  // the rock grows where it is held (and leaves from: rockThrow.ts), shards cluster over the tip
  if (p.act === 'charge') {
    const held = p.che ?? [];
    if (heldSolid(held) === 'earth') {
      drawHeldRock(ctx, cam, p, held, time);
    } else {
      const r = (0.12 + 0.2 * p.ch) * u;
      const by = tipY - 0.25 * u - Math.sin(time * 6) * 2;
      ctx.fillStyle = rgba(ELEMENT_SWATCH.ice.core, 0.85);
      for (let i = 0; i < 3; i++) {
        const a = time * 4 + (i * TAU) / 3;
        polygonPath(ctx, tipX + Math.cos(a) * r * 0.6, by + Math.sin(a) * r * 0.3, r * 0.5, 4, a);
        ctx.fill();
      }
    }
  }
  if (lying > 0) ctx.restore();

  // Rock or ice armour over the sprite body (the vector body draws its own plates above)
  if (drawn && p.arm !== 'none' && !fxAt(ctx, 'fx-status', p.arm === 'ice' ? 'armor-ice' : 'armor-rock', time, px, py)) drawArmor(ctx, px, py, u, p.arm);
  // personal bubble (DESIGN 2.5 Bubble): the effects library's gold bubble, thinning as it wears down
  const bubbleArt = p.bub > 0 && fxAt(ctx, 'fx-status', 'bubble', time + p.id * 0.2, px, py, alpha * (0.5 + 0.45 * clamp01(p.bub / 500)));
  if (p.bub > 0 && !bubbleArt) {
    const frac = clamp01(p.bub / 500);
    const r = 0.78 * u * (1 + 0.03 * Math.sin(time * 3));
    const cy = py - 0.5 * u;
    const pat = hexPattern(ctx);
    ctx.globalAlpha = alpha * (0.15 + 0.25 * frac);
    if (pat) { ctx.fillStyle = pat; circlePath(ctx, px, cy, r); ctx.fill(); }
    ctx.globalAlpha = alpha * (0.3 + 0.5 * frac);
    ctx.strokeStyle = ELEMENT_SWATCH.shield.core;
    ctx.lineWidth = 2.5;
    circlePath(ctx, px, cy, r);
    ctx.stroke();
  }
  ctx.globalAlpha = alpha;
  drawStatusAbove(ctx, px, py, 0.42 * u, (drawn ? WIZARD_H : 1.0) * u, look, time, p.facing, p.id);
  if (p.con === 0) drawPlug(ctx, px, py - 1.7 * u);
  ctx.globalAlpha = 1;
}

/** The wizard from its sprite set: the clip its state asks for, the robe in the player's colour, status washes and the hit flash on top. */
function drawWizardSprite(ctx: Ctx, cam: Camera, set: SpriteSet, p: ViewPlayer, look: StatusLook, flash: number, time: number): Drawn | null {
  const { clip, rate } = playerClip(p, look, (c) => set.has(c));
  ctx.save();
  if (look.jx !== 0 || look.jy !== 0) ctx.translate(look.jx, look.jy);
  const drawn = set.draw(ctx, cam, p.id, clip, p.facing, time, p.rx, p.ry, { rate, recolour: playerColor(p.color), tints: statusTints(look, flash) });
  ctx.restore();
  return drawn;
}

function drawArmor(ctx: Ctx, px: number, py: number, u: number, solid: 'earth' | 'ice'): void {
  if (solid === 'earth') {
    ctx.strokeStyle = ELEMENT_SWATCH.earth.core;
    ctx.lineWidth = 5;
    ctx.lineCap = 'butt';
    for (let i = 0; i < 4; i++) {
      const y = py - 0.15 * u - i * 0.17 * u;
      ctx.beginPath();
      ctx.ellipse(px, y, (0.46 - i * 0.05) * u, 0.07 * u, 0, Math.PI, TAU);
      ctx.stroke();
    }
  } else {
    ctx.strokeStyle = rgba(ELEMENT_SWATCH.ice.core, 0.9);
    ctx.fillStyle = rgba(ELEMENT_SWATCH.ice.core, 0.25);
    ctx.lineWidth = 2;
    polygonPath(ctx, px, py - 0.45 * u, 0.5 * u, 6, Math.PI / 6);
    ctx.fill();
    ctx.stroke();
  }
}

function drawPlug(ctx: Ctx, x: number, y: number): void {
  ctx.fillStyle = UI.muted;
  roundRectPath(ctx, x - 7, y - 5, 14, 10, 3);
  ctx.fill();
  ctx.fillRect(x - 4, y - 10, 2, 5);
  ctx.fillRect(x + 2, y - 10, 2, 5);
}

/** Label pass: number badge + name above the hat, HP bar + 5-slot queue + status icons under the feet (DESIGN 6.5). */
export function drawWizardLabels(ctx: Ctx, cam: Camera, p: ViewPlayer, badge: number, maxHp: number): void {
  const u = UNIT;
  const px = cam.sx(p.rx), py = cam.sy(p.ry);
  const col = playerColor(p.color);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = font(11, 700);
  const ny = py - 1.58 * u;
  const name = p.con === 0 ? `${p.name} (away)` : p.name;
  const w = ctx.measureText(name).width;
  // badge disc
  ctx.fillStyle = col;
  circlePath(ctx, px - w / 2 - 9, ny, 7);
  ctx.fill();
  ctx.fillStyle = '#000000';
  ctx.font = font(9, 800);
  ctx.fillText(`${badge}`, px - w / 2 - 9, ny + 0.5);
  ctx.font = font(11, 700);
  outlinedText(ctx, name, px, ny, p.isLocal ? UI.white : mixHex(col, '#FFFFFF', 0.4));
  drawMiniBars(ctx, px, py + 0.32 * u, p.hp, maxHp, p.q, p.bub);
  drawStatusIcons(ctx, p.st, px, py + 0.8 * u, 9);
}

/** Small HP bar and 5-slot queue bar under a wizard. */
export function drawMiniBars(ctx: Ctx, px: number, py: number, hp: number, maxHp: number, q: readonly Element[], bubble: number): void {
  const bw = 40, bh = 4;
  ctx.fillStyle = 'rgba(0,0,0,0.65)';
  ctx.fillRect(px - bw / 2 - 1, py - 1, bw + 2, bh + 2);
  const frac = maxHp > 0 ? clamp01(hp / maxHp) : 0;
  ctx.fillStyle = hpColor(frac);
  ctx.fillRect(px - bw / 2, py, bw * frac, bh);
  if (bubble > 0) {
    ctx.fillStyle = rgba(ELEMENT_SWATCH.shield.core, 0.8);
    ctx.fillRect(px - bw / 2, py, bw * clamp01(bubble / 500), 1.5);
  }
  const sw = 7, gap = 1.5;
  const x0 = px - (5 * sw + 4 * gap) / 2;
  const qy = py + bh + 3;
  for (let i = 0; i < 5; i++) {
    const el = q[i];
    const x = x0 + i * (sw + gap);
    ctx.fillStyle = el ? elementColor(el) : 'rgba(255,255,255,0.14)';
    ctx.fillRect(x, qy, sw, sw);
    if (el && !isBase(el)) {
      // A fused element gets a small dark corner mark so it reads as "two keys" at a glance.
      ctx.fillStyle = ELEMENT_SWATCH[el].dark;
      ctx.fillRect(x + 2, qy + 2, 3, 3);
    }
  }
}

// ------------------------------------------------------------------ projectiles

/** Nominal flight time (s) used to arc a projectile's height from its age. */
const BOULDER_FLIGHT_S = 0.9;
const ARROW_FLIGHT_S = 0.6;
const BOMB_LOB_S = 0.5;

/** Visible height (units) above the ground for a projectile of a given age (seconds since first seen). */
export function projectileHeight(pr: ViewProjectile, age: number): number {
  switch (pr.k) {
    case 'boulder': {
      // the server's arc (rockThrow.ts) when it sends one
      if (pr.z !== undefined) return pr.z;
      const t = Math.min(1, age / BOULDER_FLIGHT_S);
      return Math.max(0.3, 1.3 * 4 * t * (1 - t));
    }
    case 'arrow': {
      const t = Math.min(1, age / ARROW_FLIGHT_S);
      return 0.5 + 0.6 * 4 * t * (1 - t);
    }
    case 'bomb': {
      const t = Math.min(1, age / BOMB_LOB_S);
      return 1.2 * 4 * t * (1 - t) + 0.15;
    }
    case 'shard': return 0.5;
  }
}

export function drawProjectileShadow(ctx: Ctx, cam: Camera, pr: ViewProjectile, z: number): void {
  const r = Math.max(0.15, pr.r) * UNIT;
  drawShadow(ctx, cam.sx(pr.rx), cam.sy(pr.ry), r, r * 0.45, Math.max(0.15, 0.45 - z * 0.15));
}

export function drawProjectile(ctx: Ctx, cam: Camera, pr: ViewProjectile, z: number, time: number): void {
  const u = UNIT;
  const px = cam.sx(pr.rx), py = cam.sy(pr.ry) - z * u;
  const ang = Math.atan2(pr.vy, pr.vx);
  switch (pr.k) {
    case 'boulder':
      drawRock(ctx, px, py, pr.r * u, rockPayload(pr.el), rockSpin(pr.o, time));
      return;
    case 'shard': {
      if (fxTurned(ctx, 'fx-projectile', 'shard', time + pr.id * 0.1, px, py, ang)) return;
      const l = 0.28 * u, w = 0.09 * u;
      const c = Math.cos(ang), s = Math.sin(ang);
      ctx.fillStyle = ELEMENT_SWATCH.ice.core;
      ctx.beginPath();
      ctx.moveTo(px + c * l, py + s * l);
      ctx.lineTo(px - s * w, py + c * w);
      ctx.lineTo(px - c * l * 0.7, py - s * l * 0.7);
      ctx.lineTo(px + s * w, py - c * w);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(px + c * l * 0.8, py + s * l * 0.8);
      ctx.lineTo(px - c * l * 0.5, py - s * l * 0.5);
      ctx.stroke();
      return;
    }
    case 'arrow': {
      const l = 0.35 * u;
      const c = Math.cos(ang), s = Math.sin(ang);
      ctx.strokeStyle = '#8A6A3A';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(px - c * l, py - s * l);
      ctx.lineTo(px + c * l, py + s * l);
      ctx.stroke();
      ctx.fillStyle = '#DDE3EA';
      ctx.beginPath();
      ctx.moveTo(px + c * l * 1.2, py + s * l * 1.2);
      ctx.lineTo(px + c * l * 0.7 - s * 3, py + s * l * 0.7 + c * 3);
      ctx.lineTo(px + c * l * 0.7 + s * 3, py + s * l * 0.7 - c * 3);
      ctx.closePath();
      ctx.fill();
      return;
    }
    case 'bomb': {
      const r = Math.max(0.22, pr.r) * u;
      const fuse = pr.fuse ?? 1.5;
      const urgency = clamp01(1 - fuse / 1.5);
      ctx.fillStyle = '#1B1C22';
      circlePath(ctx, px, py, r);
      ctx.fill();
      ctx.strokeStyle = '#3A3D48';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      // warning glow that pulses faster as the fuse runs out
      const pulse = 0.5 + 0.5 * Math.sin(time * (6 + urgency * 24));
      ctx.globalCompositeOperation = 'lighter';
      drawGlow(ctx, ELEMENT_SWATCH.fire.core, px, py, r * (2 + pulse * 1.5 * (0.3 + urgency)));
      ctx.globalCompositeOperation = 'source-over';
      // fuse + spark
      ctx.strokeStyle = '#C9B48A';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(px, py - r);
      ctx.quadraticCurveTo(px + r * 0.5, py - r * 1.5, px + r * 0.3, py - r * 1.7);
      ctx.stroke();
      ctx.fillStyle = pulse > 0.5 ? ELEMENT_SWATCH.fire.soft : ELEMENT_SWATCH.fire.core;
      circlePath(ctx, px + r * 0.3, py - r * 1.7, 2.5);
      ctx.fill();
      return;
    }
  }
}

// ------------------------------------------------------------------ corpses and pillars

/** A dead wizard lying where it fell; rotation animates in over 0.3 s after `since` (DESIGN 4.5). */
export function drawCorpse(ctx: Ctx, cam: Camera, c: ViewCorpse, renderTime: number, time: number): void {
  const u = UNIT;
  const px = cam.sx(c.rx), py = cam.sy(c.ry);
  const fall = clamp01((renderTime - c.since) / 300);
  const col = greyed(playerColor(c.color), 0.55);
  const dark = greyed(playerColorDark(c.color), 0.55);
  // pulsing colour ring so allies can find the body (and aim Revive)
  ctx.strokeStyle = rgba(playerColor(c.color), 0.35 + 0.25 * Math.sin(time * 3));
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.ellipse(px, py, 0.7 * u, 0.3 * u, 0, 0, TAU);
  ctx.stroke();
  drawShadow(ctx, px, py, 0.6 * u, 0.2 * u, 0.35);
  const set = art.set('wizard');
  if (set && set.has('death')) {
    // The baked fall, held on its last frame and greyed like the vector corpse.
    set.draw(ctx, cam, c.id, 'death', c.facing, time, c.rx, c.ry, {
      at: Math.max(0, (renderTime - c.since) / 1000), recolour: playerColor(c.color), tints: [{ color: '#808080', alpha: 0.45 }], alpha: 0.85,
    });
  } else {
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(-Math.PI * 0.5 * fall);
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = col;
    ctx.beginPath();
    ctx.moveTo(-0.4 * u, 0);
    ctx.lineTo(0.4 * u, 0);
    ctx.lineTo(0.18 * u, -0.8 * u);
    ctx.lineTo(-0.18 * u, -0.8 * u);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = dark;
    ctx.fillRect(-0.4 * u, -0.12 * u, 0.8 * u, 0.12 * u);
    ctx.fillStyle = greyed(SKIN, 0.4);
    circlePath(ctx, 0, -0.95 * u, 0.16 * u);
    ctx.fill();
    ctx.restore();
    // hat on the floor beside the body
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = dark;
    ctx.beginPath();
    ctx.ellipse(px + 0.75 * u, py + 0.1 * u, 0.3 * u, 0.1 * u, 0, 0, TAU);
    ctx.fill();
    ctx.fillStyle = col;
    ctx.beginPath();
    ctx.moveTo(px + 0.55 * u, py + 0.08 * u);
    ctx.lineTo(px + 0.95 * u, py + 0.08 * u);
    ctx.lineTo(px + 0.8 * u, py - 0.22 * u);
    ctx.closePath();
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = font(10, 700);
  outlinedText(ctx, `${c.name}  DEAD`, px, py - 0.75 * u, UI.hpRed);
}

/** Sprite ids for the pillars (their clocks never collide with players' or enemies'). */
const PILLAR_ID = -1000;

/**
 * Upright pillar: the art library's stone column (baked like the cast, seen from the south) once its
 * set has landed; until then a front face plus a lit top ellipse. The base is in the ground layer.
 */
export function drawPillar(ctx: Ctx, cam: Camera, p: { x: number; y: number; r: number }, index = 0, time = 0): void {
  const set = art.set('pillar');
  if (set && set.draw(ctx, cam, PILLAR_ID - index, 'idle', Math.PI / 2, time, p.x, p.y)) return;
  const u = UNIT;
  const px = cam.sx(p.x), py = cam.sy(p.y);
  const r = p.r * u, h = PILLAR_H * u;
  ctx.fillStyle = GROUND.pillarFace;
  ctx.fillRect(px - r, py - h, r * 2, h);
  // Flat shading strips instead of a per-frame gradient (gradients are cached or avoided).
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fillRect(px - r, py - h, r * 0.35, h);
  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  ctx.fillRect(px - r * 0.3, py - h, r * 0.5, h);
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.fillRect(px + r * 0.55, py - h, r * 0.45, h);
  ctx.fillStyle = GROUND.pillarFace;
  ctx.beginPath();
  ctx.ellipse(px, py, r, r * 0.45, 0, 0, Math.PI);
  ctx.fill();
  ctx.fillStyle = GROUND.pillarTop;
  ctx.beginPath();
  ctx.ellipse(px, py - h, r, r * 0.45, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = GROUND.wallEdge;
  ctx.lineWidth = 1;
  ctx.stroke();
}
